import React, { useState } from 'react';
import { Card, Input, Button, Form, message, Progress, Row, Col, Typography, Select, Checkbox, Space, Badge, Collapse } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, UserOutlined, CheckCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import apiClient from '../../server/server';
import EventFormSection from './EventFormSection';

const { Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

// Individual Event Form Component
const IndividualEventForm = ({ 
  eventData, 
  index, 
  searchResults, 
  profilesLoading, 
  searchLoading, 
  handleProfileSearch, 
  countries, 
  cities, 
  handleCountryChange, 
  handleAddressChange, 
  musicGenres, 
  generateAIDescription 
}) => {
  const [form] = Form.useForm();
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [localCities, setLocalCities] = useState([]);

  React.useEffect(() => {
    // Pre-populate form when component mounts
    const formValues = {
      title: eventData.data.name,
      description: eventData.data.description,
      organisationName: eventData.data.promoter,
      tags: eventData.data.lineup || [],
      startTime: eventData.data.dateTime ? moment(eventData.data.dateTime) : null,
      endTime: eventData.data.endDateTime ? moment(eventData.data.endDateTime) : null,
      publishTime: moment(),
      flyer: eventData.data.image,
      cover: eventData.data.image,
      musicTypes: [],
      eventType: 'Club',
      media: [],
      musicList: [],
      videoStory: '',
      embedmix: '',
      localisation: {
        address: eventData.data.address || '',
        coordinates: [0, 0]
      },
      externalLink: eventData.url
    };
    
    form.setFieldsValue(formValues);
  }, [eventData, form]);

  const handleCreateEvent = async (values) => {
    try {
      if (!values.creatorProfileId) {
        message.error('Please select a creator profile');
        return;
      }

      const eventPayload = {
        ...values,
        isPublished: true,
        localisation: values.localisation || {
          coordinates: [0, 0],
          address: '',
        },
        isPrivate: false,
        includeChat: true,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        publishTime: values.publishTime?.toISOString(),
        musicTypes: Array.isArray(values.musicTypes) ? values.musicTypes : [],
        tags: Array.isArray(values.tags) ? values.tags : [],
        affiliatedProfiles: Array.isArray(values.affiliatedProfiles) ? values.affiliatedProfiles : [],
        media: Array.isArray(values.media) ? values.media : [],
        musicList: Array.isArray(values.musicList) ? values.musicList : [],
        eventType: values.eventType || 'Club',
        description: values.description || '',
        organisationName: values.organisationName || '',
        embedmix: values.embedmix || '',
        cover: values.cover || null,
      };

      if (eventPayload.localisation?.localisation) {
        eventPayload.localisation = eventPayload.localisation.localisation;
      }

      await apiClient.post('/backoffice/events/create', eventPayload);
      message.success(`Event "${eventData.data.name}" created successfully!`);
    } catch (error) {
      message.error(`Failed to create event: ${error.response?.data?.message || error.message}`);
    }
  };

  // Handle profile selection with auto-population logic (same as single scraper)
  const handleProfileSelect = (value) => {
    const selected = searchResults.find(p => p._id === value);
    setSelectedProfile(selected);
    
    // Auto-select music types from the selected profile if event music types are empty
    const currentMusicTypes = form.getFieldValue('musicTypes') || [];
    if (currentMusicTypes.length === 0 && selected?.musicTypes?.length > 0) {
      form.setFieldsValue({ musicTypes: selected.musicTypes });
    }

    // Auto-select country and city from the selected profile if not already set
    const currentCountry = form.getFieldValue('country');
    const currentCity = form.getFieldValue('city');
    
    if (!currentCountry && selected?.country) {
      // Find the country in our countries list
      const profileCountry = countries.find(c => 
        c._id === selected.country._id || 
        c._id === selected.country ||
        c.name === selected.country.name ||
        c.code === selected.country.code
      );
      
      if (profileCountry) {
        form.setFieldsValue({ country: profileCountry._id });
        setLocalCities(profileCountry.cities || []);
        
        // Auto-select city if profile has one and it exists in the country's cities
        if (!currentCity && selected?.city && profileCountry.cities) {
          const profileCity = profileCountry.cities.find(city => 
            city._id === selected.city._id || 
            city._id === selected.city ||
            city.name === selected.city.name
          );
          
          if (profileCity) {
            form.setFieldsValue({ city: profileCity._id });
          }
        }
      }
    }
  };

  // Handle country change for this specific form
  const handleLocalCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      setLocalCities(selectedCountry.cities || []);
      form.setFieldsValue({ city: null }); // Reset city when country changes
    }
  };

  return (
    <div className="p-4 border rounded bg-gray-50">
      <p className="mb-4 text-sm text-gray-600">
        Customize this event before creating. All fields are pre-filled from scraped data.
        <strong> Selecting a creator profile will auto-populate music types, country, and city if they're empty.</strong>
      </p>
      <EventFormSection
        form={form}
        loading={false}
        handleCreateEvent={handleCreateEvent}
        setShowEventForm={() => {}}
        setHasScrapedAddress={() => {}}
        setMediaUploadRef={() => {}}
        setVideoStoryUploadRef={() => {}}
        selectedProfile={selectedProfile}
        searchResults={searchResults}
        profilesLoading={profilesLoading}
        searchLoading={searchLoading}
        handleProfileSearch={handleProfileSearch}
        handleProfileSelect={handleProfileSelect}
        countries={countries}
        cities={localCities}
        handleCountryChange={handleLocalCountryChange}
        handleAddressChange={handleAddressChange}
        hasScrapedAddress={false}
        musicGenres={musicGenres}
        aiLoading={false}
        generateAIDescription={generateAIDescription}
      />
    </div>
  );
};

const BatchEventScraper = ({ 
  profiles, 
  profilesLoading, 
  searchProfiles, 
  musicGenres,
  countries,
  cities,
  handleCountryChange,
  handleAddressChange,
  selectedCountryId,
  setCities,
  setSelectedCountryId,
  generateAIDescription
}) => {
  const [loading, setLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [creatingEvents, setCreatingEvents] = useState(false);
  const [createdEvents, setCreatedEvents] = useState([]);
  const [failedEvents, setFailedEvents] = useState([]);
  
  const [batchForm] = Form.useForm();

  // Search state for profiles
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Handle batch scraping
  const handleBatchScrape = async (values) => {
    try {
      setLoading(true);
      setProgress({ current: 0, total: 0 });
      
      const response = await axios.post('http://localhost:3002/batch/scrape-ra-profile', {
        url: values.profileUrl,
        maxEvents: values.maxEvents || undefined,
        fullDetails: true
      });

      setBatchResults(response.data);
      
      // Select all events by default
      const selected = response.data.events.map((_, index) => index);
      setSelectedEvents(selected);
      
      message.success(`Successfully scraped ${response.data.successfulScrapes} events from profile!`);
      
    } catch (error) {
      message.error('Failed to scrape profile: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle individual event selection
  const handleEventSelection = (eventIndex, checked) => {
    if (checked) {
      setSelectedEvents([...selectedEvents, eventIndex]);
    } else {
      setSelectedEvents(selectedEvents.filter(i => i !== eventIndex));
    }
  };

  // Handle select all/none
  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedEvents(batchResults.events.map((_, index) => index));
    } else {
      setSelectedEvents([]);
    }
  };

  // Create individual event from quick action
  const createSingleEvent = async (eventIndex) => {
    try {
      const eventData = batchResults.events[eventIndex];
      
      // Create basic event payload from scraped data - no profile required for quick creation
      const eventPayload = {
        title: eventData.data.name,
        description: eventData.data.description || `Event at ${eventData.data.location}`,
        organisationName: eventData.data.promoter,
        tags: eventData.data.lineup || [],
        startTime: eventData.data.dateTime ? moment(eventData.data.dateTime).toISOString() : moment().toISOString(),
        endTime: eventData.data.endDateTime ? moment(eventData.data.endDateTime).toISOString() : moment().add(6, 'hours').toISOString(),
        publishTime: moment().toISOString(),
        flyer: eventData.data.image,
        cover: eventData.data.image,
        musicTypes: [],
        eventType: 'Club',
        media: [],
        musicList: [],
        videoStory: '',
        embedmix: '',
        externalLink: eventData.url,
        isPublished: true,
        localisation: {
          coordinates: [0, 0],
          address: eventData.data.address || '',
        },
        isPrivate: false,
        includeChat: true,
        affiliatedProfiles: [],
        // Use a default creator profile if available, or leave empty
        creatorProfileId: profiles && profiles.length > 0 ? profiles[0]._id : undefined
      };

      if (eventPayload.localisation?.localisation) {
        eventPayload.localisation = eventPayload.localisation.localisation;
      }

      await apiClient.post('/backoffice/events/create', eventPayload);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Unknown error occurred');
    }
  };

  // Create all selected events
  const handleCreateAllEvents = async () => {
    if (selectedEvents.length === 0) {
      message.warning('Please select at least one event to create');
      return;
    }

    setCreatingEvents(true);
    setCreatedEvents([]);
    setFailedEvents([]);
    
    const total = selectedEvents.length;
    let completed = 0;

    for (const eventIndex of selectedEvents) {
      try {
        await createSingleEvent(eventIndex);
        setCreatedEvents(prev => [...prev, eventIndex]);
        completed++;
        setProgress({ current: completed, total });
        message.success(`Event ${eventIndex + 1} created successfully (${completed}/${total})`);
      } catch (error) {
        setFailedEvents(prev => [...prev, { index: eventIndex, error: error.message }]);
        message.error(`Failed to create event ${eventIndex + 1}: ${error.message}`);
      }
    }

    setCreatingEvents(false);
    
    if (createdEvents.length + 1 >= selectedEvents.length) { // +1 because state might not be updated yet
      message.success(`All ${selectedEvents.length} events created successfully!`);
    }
  };

  // Profile search functionality
  const handleProfileSearch = async (value) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (!value || value.trim() === '') {
      setSearchResults(profiles);
      setSearchLoading(false);
      return;
    }
    
    setSearchLoading(true);
    
    const timeout = setTimeout(async () => {
      try {
        const results = await searchProfiles(value.trim());
        setSearchResults(results);
      } catch (error) {
        message.error('Failed to search profiles');
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    
    setSearchTimeout(timeout);
  };

  const renderEventCard = (eventData, index) => {
    const isSelected = selectedEvents.includes(index);
    const isCreated = createdEvents.includes(index);
    const failedEvent = failedEvents.find(e => e.index === index);
    
    return (
      <Card
        key={index}
        size="small"
        className={`mb-4 ${isSelected ? 'border-blue-400' : ''} ${isCreated ? 'border-green-400' : ''} ${failedEvent ? 'border-red-400' : ''}`}
        title={
          <div className="flex items-center justify-between">
            <Checkbox 
              checked={isSelected}
              onChange={(e) => handleEventSelection(index, e.target.checked)}
              disabled={creatingEvents}
            >
              <Text strong>{eventData.data.name}</Text>
            </Checkbox>
            {isCreated && <Badge status="success" text="Created" />}
            {failedEvent && <Badge status="error" text="Failed" />}
          </div>
        }
        extra={
          <Button
            size="small"
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={async () => {
              try {
                await createSingleEvent(index);
                setCreatedEvents(prev => [...prev, index]);
                message.success(`Event "${eventData.data.name}" created successfully!`);
              } catch (error) {
                setFailedEvents(prev => [...prev, { index, error: error.message }]);
                message.error(`Failed to create event: ${error.message}`);
              }
            }}
            loading={creatingEvents}
            disabled={!isSelected || isCreated}
          >
            {isCreated ? 'Created' : 'Create Event'}
          </Button>
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            {eventData.data.image && (
              <img 
                src={eventData.data.image} 
                alt={eventData.data.name}
                style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }}
              />
            )}
          </Col>
          <Col span={18}>
            <Space direction="vertical" size="small" className="w-full">
              <Space size="small">
                <ClockCircleOutlined />
                <Text>
                  {moment(eventData.data.dateTime).format('MMM DD, YYYY HH:mm')}
                  {eventData.data.endDateTime && ` - ${moment(eventData.data.endDateTime).format('HH:mm')}`}
                </Text>
              </Space>
              
              <Space size="small">
                <EnvironmentOutlined />
                <Text>{eventData.data.location}</Text>
              </Space>
              
              {eventData.data.promoter && (
                <Space size="small">
                  <UserOutlined />
                  <Text>{eventData.data.promoter}</Text>
                </Space>
              )}
              
              {eventData.data.lineup && eventData.data.lineup.length > 0 && (
                <Text className="text-sm">
                  <strong>Lineup:</strong> {eventData.data.lineup.join(', ')}
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div>
      {/* Batch Scraping Form */}
      <Card title="Batch RA Profile Scraper" className="mb-4">
        <Form form={batchForm} onFinish={handleBatchScrape} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="profileUrl"
                label="RA Profile URL"
                rules={[
                  { required: true, message: 'Please input the RA profile URL!' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
                help="Examples: https://ra.co/clubs/138829/events, https://ra.co/promoters/15319/events, https://ra.co/dj/artist/events"
              >
                <Input placeholder="https://ra.co/clubs/138829/events" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxEvents"
                label="Max Events (optional)"
                help="Leave empty to scrape all events"
              >
                <Select placeholder="All events" allowClear>
                  <Option value={5}>5 events</Option>
                  <Option value={10}>10 events</Option>
                  <Option value={20}>20 events</Option>
                  <Option value={50}>50 events</Option>
                  <Option value={100}>100 events</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              Scrape Profile Events
            </Button>
          </Form.Item>
        </Form>
        
        {loading && (
          <div className="text-center mt-4">
            <Progress 
              percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0} 
              status="active"
            />
            <p className="mt-2">Scraping events from profile...</p>
          </div>
        )}
      </Card>

      {/* Batch Results */}
      {batchResults && (
        <Card 
          title={
            <div className="flex justify-between items-center">
              <span>Scraped Events ({batchResults.events.length})</span>
              <Space>
                <Button 
                  onClick={() => handleSelectAll(true)}
                  disabled={creatingEvents}
                >
                  Select All
                </Button>
                <Button 
                  onClick={() => handleSelectAll(false)}
                  disabled={creatingEvents}
                >
                  Select None
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleCreateAllEvents}
                  loading={creatingEvents}
                  disabled={selectedEvents.length === 0}
                >
                  Create Selected Events ({selectedEvents.length})
                </Button>
              </Space>
            </div>
          }
          className="mb-4"
        >
          {/* Progress bar for batch creation */}
          {creatingEvents && (
            <div className="mb-4">
              <Progress 
                percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}
                status="active"
                format={() => `${progress.current}/${progress.total} events created`}
              />
            </div>
          )}

          {/* Stats */}
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card size="small" className="text-center">
                <Text strong className="text-lg">{batchResults.totalEvents}</Text>
                <br />
                <Text type="secondary">Total Found</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" className="text-center">
                <Text strong className="text-lg text-blue-600">{selectedEvents.length}</Text>
                <br />
                <Text type="secondary">Selected</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" className="text-center">
                <Text strong className="text-lg text-green-600">{createdEvents.length}</Text>
                <br />
                <Text type="secondary">Created</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small" className="text-center">
                <Text strong className="text-lg text-red-600">{failedEvents.length}</Text>
                <br />
                <Text type="secondary">Failed</Text>
              </Card>
            </Col>
          </Row>

          {/* Event Cards */}
          <div className="max-h-96 overflow-y-auto">
            {batchResults.events.map((eventData, index) => renderEventCard(eventData, index))}
          </div>

          {/* Failed Events Details */}
          {failedEvents.length > 0 && (
            <Card className="mt-4" title="Failed Events" size="small">
              {failedEvents.map(({ index, error }) => (
                <div key={index} className="mb-2 p-2 bg-red-50 rounded">
                  <Text strong>{batchResults.events[index].data.name}</Text>
                  <br />
                  <Text type="danger" className="text-sm">{error}</Text>
                </div>
              ))}
            </Card>
          )}
        </Card>
      )}

      {/* Individual Event Forms (Collapsed) */}
      {batchResults && (
        <Collapse className="mt-4">
          <Panel header="Advanced: Individual Event Forms" key="1">
            <Text type="secondary" className="block mb-4">
              Use these forms to customize individual events before creation. 
              You can modify any field, add profiles, music types, etc. Each form has its own state.
            </Text>
            
            {batchResults.events.map((eventData, index) => (
              <Card
                key={index}
                title={`Event ${index + 1}: ${eventData.data.name}`}
                className="mb-4"
                size="small"
              >
                <IndividualEventForm
                  eventData={eventData}
                  index={index}
                  searchResults={searchResults}
                  profilesLoading={profilesLoading}
                  searchLoading={searchLoading}
                  handleProfileSearch={handleProfileSearch}
                  countries={countries}
                  cities={cities}
                  handleCountryChange={handleCountryChange}
                  handleAddressChange={handleAddressChange}
                  musicGenres={musicGenres}
                  generateAIDescription={generateAIDescription}
                />
              </Card>
            ))}
          </Panel>
        </Collapse>
      )}
    </div>
  );
};

export default BatchEventScraper;